/**
 * Sonner Toast Component — the ONE toaster every web app mounts.
 *
 * `theme` is the APP's resolved theme and is required. Sonner's own default
 * follows the operating system, so a dark app on a light OS showed light
 * toasts; the app knows which theme it is actually drawing, and says so.
 *
 * The four status colours come from `TOAST_COLORS` in `@tonnie/design-tokens`,
 * not from sonner: its light success text was 4.29:1 on its own background
 * (WCAG AA needs 4.5:1). They are set as the CSS variables sonner reads, on its
 * toaster element, where they win over its stylesheet. Plain toasts use the
 * app's popover surface. `style` is not accepted: the palette is the point.
 */

import * as React from 'react';
import { TOAST_COLORS, type ToastTheme, type ToastVariant } from '@tonnie/design-tokens';
import { Toaster as Sonner, type ToasterProps } from 'sonner';

const VARIANTS: readonly ToastVariant[] = ['success', 'error', 'warning', 'info'];

/** The CSS variables sonner reads, for one theme. */
export function toasterStyle(theme: ToastTheme): React.CSSProperties {
  const vars: Record<string, string> = {
    '--normal-bg': 'var(--popover)',
    '--normal-text': 'var(--popover-foreground)',
    '--normal-border': 'var(--border)',
  };
  for (const variant of VARIANTS) {
    const colors = TOAST_COLORS[theme][variant];
    vars[`--${variant}-bg`] = colors.bg;
    vars[`--${variant}-border`] = colors.border;
    vars[`--${variant}-text`] = colors.text;
  }
  return vars as React.CSSProperties;
}

export interface ToasterWrapperProps extends Omit<ToasterProps, 'theme' | 'style'> {
  /** The theme the app is drawing — its resolved light/dark, never "system". */
  theme: ToastTheme;
}

const Toaster = ({ theme, ...props }: ToasterWrapperProps) => {
  return <Sonner theme={theme} className="toaster group" style={toasterStyle(theme)} {...props} />;
};

export { Toaster };
